import React from "react";
import { IPMS247BookingWidget } from "@/components/IPMS247BookingWidget";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

/**
 * Test page to showcase all IPMS247 Booking Widget examples
 * Accessible at /test-booking-widget
 */
const BookingWidgetTest = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-oid="_9d3s_-">
      <SEO
        title="Booking Widget Test Page"
        description="Test page showcasing IPMS247 booking widget examples"
        canonical="/test-booking-widget"
        data-oid="nqxe7-0"
      />

      <NewHeader data-oid="w3.-9um" />

      <main className="pt-8 pb-20" data-oid="qwd6dif">
        {/* Page Header */}
        <div
          className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12"
          data-oid="7qqiep4"
        >
          <div
            className="max-w-7xl mx-auto px-4 text-center"
            data-oid="i-km-5b"
          >
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              data-oid="kb6x04u"
            >
              IPMS247 Booking Widget Examples
            </h1>
            <p className="text-xl text-blue-100" data-oid="gw55wea">
              Test different configurations and layouts of the booking widget
            </p>
          </div>
        </div>

        <div
          className="max-w-7xl mx-auto px-4 py-12 space-y-16"
          data-oid="5tl1_sc"
        >
          {/* Example 1: Basic Usage */}
          <section
            className="bg-white rounded-lg shadow-md p-8"
            data-oid="d33bxel"
          >
            <div className="mb-6" data-oid="xnkze-k">
              <h2
                className="text-3xl font-bold text-gray-900 mb-2"
                data-oid="tw:wgbj"
              >
                Example 1: Basic Usage
              </h2>
              <p className="text-gray-600" data-oid="2j0twq:">
                Simplest implementation with default configuration
              </p>
            </div>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50"
              data-oid="1c8widi"
            >
              <IPMS247BookingWidget data-oid="8b5w1r_" />
            </div>
          </section>

          {/* Example 2: Custom Styled */}
          <section
            className="bg-gradient-to-b from-white to-gray-50 rounded-lg shadow-md p-8"
            data-oid="uwlcn5f"
          >
            <div className="mb-6" data-oid="kdkpq5.">
              <h2
                className="text-3xl font-bold text-gray-900 mb-2"
                data-oid="x:y0f0c"
              >
                Example 2: Custom Styled Widget
              </h2>
              <p className="text-gray-600" data-oid="1f19g7w">
                Custom colors, width, and text labels with additional styling
              </p>
            </div>
            <div className="max-w-4xl mx-auto" data-oid="df-ol8v">
              <IPMS247BookingWidget
                className="shadow-lg rounded-lg p-6 bg-white"
                config={{
                  headerColor: "#75dbe0",
                  bodyColor: "#ddf0ef",
                  boxwidth: "100%",
                  boxwidthtype: "TYPE_PCT",
                  HTextCaption: "Make a Reservation",
                  BtnTextCaption: "Check Availability",
                }}
                data-oid="86y4iks"
              />
            </div>
          </section>

          {/* Example 3: Accommodations Page Layout */}
          <section
            className="bg-white rounded-lg shadow-md p-8"
            data-oid="bz-vdp_"
          >
            <div className="mb-6" data-oid="vra_2z.">
              <h2
                className="text-3xl font-bold text-gray-900 mb-2"
                data-oid="2qpew_b"
              >
                Example 3: Accommodations Page Layout
              </h2>
              <p className="text-gray-600" data-oid="poqw3mm">
                Two-column layout with room details and sticky booking widget
              </p>
            </div>
            <div
              className="grid grid-cols-1 lg:grid-cols-2 gap-8"
              data-oid="_e23vsh"
            >
              {/* Room details on the left */}
              <div className="space-y-4" data-oid="t5craed">
                <h3
                  className="text-2xl font-bold text-gray-900"
                  data-oid="hotv261"
                >
                  Family Suite
                </h3>
                <p className="text-gray-600" data-oid="qfb0_mc">
                  Spacious suite perfect for families with stunning lake views.
                  Features include a separate living area, fully equipped
                  kitchen, and private balcony overlooking Lake Thun.
                </p>
                <ul
                  className="list-disc list-inside text-gray-600 space-y-2"
                  data-oid="022:_8a"
                >
                  <li data-oid="soy-9.f">120 m² / 1,292 sq ft</li>
                  <li data-oid="dm_oi97">Sleeps up to 6 guests</li>
                  <li data-oid="lsofc34">Lake view balcony</li>
                  <li data-oid="ik2vz3j">Full kitchen</li>
                  <li data-oid="k4t92zt">Separate living area</li>
                </ul>
              </div>

              {/* Booking widget on the right */}
              <div className="lg:sticky lg:top-8 h-fit" data-oid=":woo2ev">
                <IPMS247BookingWidget
                  className="bg-white rounded-lg shadow-xl p-6 border border-gray-200"
                  config={{
                    boxwidth: "100%",
                    boxwidthtype: "TYPE_PCT",
                    promotion: true,
                    ShowNights: true,
                    Nonights: 15,
                  }}
                  data-oid="jj.ob6_"
                />
              </div>
            </div>
          </section>

          {/* Example 4: Hero Section */}
          <section
            className="relative rounded-lg shadow-md overflow-hidden"
            data-oid="rhbehy:"
          >
            <div
              className="absolute inset-0 bg-gradient-to-r from-blue-900 to-blue-700 opacity-90"
              data-oid="kuj4dr0"
            />

            <div className="relative z-10 p-8 md:p-16" data-oid="cn5ab8k">
              <div className="max-w-4xl mx-auto text-center" data-oid="vc0cl_-">
                <h2
                  className="text-3xl font-bold text-white mb-2"
                  data-oid="gr16egy"
                >
                  Example 4: Hero Section Widget
                </h2>
                <p className="text-xl text-blue-100 mb-8" data-oid="7_son8p">
                  Compact widget embedded in a hero section with background
                </p>

                {/* Compact booking widget */}
                <div className="max-w-2xl mx-auto" data-oid="0f1h22l">
                  <IPMS247BookingWidget
                    className="bg-white rounded-lg shadow-2xl p-4"
                    config={{
                      boxwidth: "100%",
                      boxwidthtype: "TYPE_PCT",
                      ShowHeader: "1",
                      HTextCaption: "Book Your Stay",
                    }}
                    data-oid="woqm_:2"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Example 5: Multiple Widgets */}
          <section
            className="bg-white rounded-lg shadow-md p-8"
            data-oid="-2qqq6c"
          >
            <div className="mb-6" data-oid="7r5w1my">
              <h2
                className="text-3xl font-bold text-gray-900 mb-2"
                data-oid="i62mn9j"
              >
                Example 5: Multiple Widgets
              </h2>
              <p className="text-gray-600" data-oid=":ssevfa">
                Using multiple widgets on the same page with different
                configurations
              </p>
            </div>
            <div className="space-y-12" data-oid="acvuqwn">
              {/* Full-featured widget */}
              <div data-oid="xzgeq21">
                <h3
                  className="text-2xl font-bold text-gray-900 mb-4"
                  data-oid="e.lix4k"
                >
                  Full-Featured Widget
                </h3>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50"
                  data-oid="cmgd2et"
                >
                  <IPMS247BookingWidget
                    containerId="booking-widget-full"
                    config={{
                      promotion: true,
                      ShowNights: true,
                      ShowChild: true,
                      rooms: true,
                      boxwidth: "100%",
                      boxwidthtype: "TYPE_PCT",
                    }}
                    data-oid="cfn31gk"
                  />
                </div>
              </div>

              {/* Simplified widget */}
              <div data-oid="fpa0sx7">
                <h3
                  className="text-2xl font-bold text-gray-900 mb-4"
                  data-oid="y3uwavq"
                >
                  Simplified Quick Booking
                </h3>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50"
                  data-oid=":xzmnxc"
                >
                  <div className="max-w-2xl mx-auto" data-oid="xq0_tst">
                    <IPMS247BookingWidget
                      containerId="booking-widget-quick"
                      config={{
                        promotion: false,
                        ShowNights: false,
                        ShowChild: false,
                        rooms: false,
                        boxwidth: "600",
                        boxwidthtype: "TYPE_PX",
                      }}
                      data-oid="wjvfm7o"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Info Section */}
          <section
            className="bg-blue-50 rounded-lg p-8 border border-blue-200"
            data-oid="b1cfh7h"
          >
            <h2
              className="text-2xl font-bold text-gray-900 mb-4"
              data-oid="ee:9dma"
            >
              📝 Implementation Notes
            </h2>
            <ul className="space-y-2 text-gray-700" data-oid="c:59jsi">
              <li className="flex items-start" data-oid="7s10svi">
                <span className="font-semibold mr-2" data-oid="jm-q7xa">
                  •
                </span>
                <span data-oid="49e:d2.">
                  Each widget automatically loads required CSS and JavaScript
                  files
                </span>
              </li>
              <li className="flex items-start" data-oid="pm4tazc">
                <span className="font-semibold mr-2" data-oid="70amh3k">
                  •
                </span>
                <span data-oid="lxl-qih">
                  When using multiple widgets, each must have a unique{" "}
                  <code className="bg-blue-100 px-1 rounded" data-oid="-7-atw-">
                    containerId
                  </code>
                </span>
              </li>
              <li className="flex items-start" data-oid="5nid0um">
                <span className="font-semibold mr-2" data-oid="yz9tyd2">
                  •
                </span>
                <span data-oid="_1f262m">
                  Widgets are fully customizable via the{" "}
                  <code className="bg-blue-100 px-1 rounded" data-oid="aq5z-6_">
                    config
                  </code>{" "}
                  prop
                </span>
              </li>
              <li className="flex items-start" data-oid="p3:vucq">
                <span className="font-semibold mr-2" data-oid="0f2.7_7">
                  •
                </span>
                <span data-oid="acz14a0">
                  All configuration options from the original IPMS247 code are
                  supported
                </span>
              </li>
              <li className="flex items-start" data-oid="dwl6:kk">
                <span className="font-semibold mr-2" data-oid="8_0nsev">
                  •
                </span>
                <span data-oid="05k48kz">
                  Widgets are responsive and work on all screen sizes
                </span>
              </li>
            </ul>
          </section>
        </div>
      </main>

      <Footer data-oid="0o8_ldl" />
    </div>
  );
};

export default BookingWidgetTest;
